import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { formatMoney } from '@atek/estimate-pricing';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatDate } from '@/lib/utils';
import { estimateLabel, uiLang, year1Range } from '../lib/format';
import type { EstimateSummary } from '../types';
import { EstimateStatusBadge } from './EstimateStatusBadge';

interface Props {
  rows: EstimateSummary[];
  /** Company and opportunity columns (list page); the opportunity tab leaves them out. */
  showContext?: boolean;
  /** Owner names by PostgreSQL user id; the owner column shows when given. */
  ownerNames?: Map<string, string>;
  /** Year 1 of each budget instead of the range (opportunity tab). */
  perBudget?: boolean;
}

export function EstimatesTable({ rows, showContext = false, ownerNames, perBudget = false }: Props) {
  const { t, i18n } = useTranslation('crm');
  const lang = uiLang(i18n.language);
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('estimates.columns.number')}</TableHead>
          <TableHead>{t('estimates.columns.title')}</TableHead>
          {showContext && <TableHead>{t('estimates.columns.company')}</TableHead>}
          {showContext && <TableHead>{t('estimates.columns.opportunity')}</TableHead>}
          {ownerNames && <TableHead>{t('estimates.columns.owner')}</TableHead>}
          <TableHead>{t('estimates.columns.status')}</TableHead>
          <TableHead className="text-right">{t('estimates.columns.year1')}</TableHead>
          <TableHead>{t('estimates.columns.deck')}</TableHead>
          <TableHead>{t('estimates.columns.updated')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="font-medium whitespace-nowrap">
              <Link to={`/crm/estimates/${row.id}`} className="text-primary hover:underline">{estimateLabel(row)}</Link>
            </TableCell>
            <TableCell>{row.title}</TableCell>
            {showContext && <TableCell>{row.companyName ?? '—'}</TableCell>}
            {showContext && (
              <TableCell>
                <Link to={`/crm/opportunities/${row.opportunityId}`} className="hover:underline">{row.opportunityTitle ?? '—'}</Link>
              </TableCell>
            )}
            {ownerNames && <TableCell>{(row.ownerUserId && ownerNames.get(row.ownerUserId)) || '—'}</TableCell>}
            <TableCell><EstimateStatusBadge status={row.status} /></TableCell>
            <TableCell className="text-right whitespace-nowrap">
              {perBudget
                ? row.budgets.map((b) => <div key={b.key}>{b.label}: {formatMoney(b.year1, row.currency, lang)}</div>)
                : year1Range(row.budgets, row.currency, lang)}
            </TableCell>
            <TableCell>
              {row.deckUrl ? (
                <a href={row.deckUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                  {t('estimates.deck.open')} <ExternalLink className="h-3 w-3" />
                </a>
              ) : '—'}
            </TableCell>
            <TableCell className="whitespace-nowrap">{formatDate(row.updatedAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
